import g from '#core/global';
import load from '#core/load';


const hero_$ = '.about-hero';
const toggle_$ = '.button--transport--about-hero';
const marquee_$ = '.marquee';
const images_layer_$ = '.about-hero__layer--images';
const image_$ = 'img';

const playing_attr = 'data-playing';
const images_ready_attr = 'data-images-ready';
const marquee_ready_attr = 'data-marquee-ready';
const last_width_attr = 'data-last-width';
const speed_attr = 'data-speed';

// Master toggle for the aspect-ratio-driven height bias. Flip to
// `false` to draw heights uniformly across the full [height_min,
// height_max] range regardless of each image's aspect ratio.
const use_height_bias = true;

const height_prop = '--about-hero-image-height';
const shift_prop = '--about-hero-image-shift';
const shift_max_prop = '--about-hero-image-shift-max';

// Random height range, in px.
const height_min = 280;
const height_max = 464;
// ±jitter around the ratio-driven target. Wide enough that all-square
// galleries still produce visible variation (target=372 ± 80 → 292–452).
const height_jitter = 80;
// Aspect ratio pivots for the height bias, centered on square (1.0).
// ratio ≤ 0.7 (tall portrait) → bias=0 → target=height_min;
// ratio ≥ 1.3 (wide landscape) → bias=1 → target=height_max;
// ratio = 1.0 (square)          → bias=0.5 → target=372.
const ratio_low = 0.7;
const ratio_high = 1.3;

// Per-image shift cap, in percent of the image's own height. The cap
// scales linearly with the randomized height: the shortest images can
// travel the furthest (so they still feel like they're moving), the
// tallest images barely move (so they don't bleed too far).
const shift_max_pct_at_short = 50;  // at height_min
const shift_max_pct_at_tall  = 15;  // at height_max


let has_hero = false;
let $heroes = null;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('about-hero')) return false;

		load.content(() => {
			has_hero = assign_elements();
			if (!has_hero) return false;

			$heroes.forEach($hero => {
				randomize_images($hero);
			});
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_hero) return true;

	$heroes = document.querySelectorAll(hero_$);
	if (!$heroes.length) return false;

	$heroes.forEach($hero => {
		const $toggle = $hero.querySelector(toggle_$);
		if (!$toggle) return;

		$toggle.addEventListener(g.click_e, () => {
			toggle_state($hero);
		});
	});

	return true;
}


/**
 * Read the current playing state of the hero
 * @param {element} $hero
 * @return {boolean}
 */
function is_playing($hero) {
	return $hero.getAttribute(playing_attr) === g.true_s;
}


/**
 * Flip playing state on the hero and mirror it to every child marquee,
 * so all three stacked layers pause and resume in lockstep.
 * @param {element} $hero
 */
function toggle_state($hero) {
	const next = is_playing($hero) ? g.false_s : g.true_s;
	$hero.setAttribute(playing_attr, next);

	$hero.querySelectorAll(marquee_$).forEach($marquee => {
		$marquee.setAttribute(playing_attr, next);
	});
}


/**
 * Wait for an <img> to know its natural dimensions, then resolve.
 * @param {element} $img
 * @return {Promise}
 */
function image_ready($img) {
	return new Promise(resolve => {
		if ($img.complete && $img.naturalWidth > 0) {
			resolve();
			return;
		}
		$img.addEventListener('load', () => resolve(), { once: true });
		$img.addEventListener('error', () => resolve(), { once: true });
	});
}


/**
 * Clamp helper
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
function clamp(value, min, max) {
	return Math.max(min, Math.min(max, value));
}


/**
 * Compute a random height for one image based on its aspect ratio.
 * Wider ratios bias toward `height_max`; portrait ratios bias toward
 * `height_min`. Jitter ensures sets of same-ratio images still vary.
 * @param {number} ratio  naturalWidth / naturalHeight
 * @return {number} whole-pixel height
 */
function compute_height(ratio) {
	const bias = clamp((ratio - ratio_low) / (ratio_high - ratio_low), 0, 1);
	const target = height_min + (height_max - height_min) * bias;
	const jitter = (Math.random() * 2 - 1) * height_jitter;
	return Math.round(clamp(target + jitter, height_min, height_max));
}


/**
 * Compute a uniform random height across the full [height_min,
 * height_max] range — used when the hero has the aspect-ratio bias
 * toggled off via `data-use-height-bias="false"`.
 * @return {number} whole-pixel height
 */
function compute_height_uniform() {
	return Math.round(height_min + Math.random() * (height_max - height_min));
}


/**
 * Compute a random vertical shift in the range −1..1.
 * @return {number}
 */
function compute_shift() {
	return Math.random() * 2 - 1;
}


/**
 * Compute the per-image max-shift cap (in percent of the image's own
 * height). Linearly interpolates between the short/tall endpoints
 * based on where this image's randomized height sits in [height_min,
 * height_max].
 * @param {number} height
 * @return {number}
 */
function compute_shift_max(height) {
	const norm = clamp(
		(height - height_min) / (height_max - height_min),
		0,
		1
	);
	return shift_max_pct_at_short
		+ norm * (shift_max_pct_at_tall - shift_max_pct_at_short);
}


/**
 * For each <img> in the hero's image layer, wait for it to load, then
 * set inline CSS variables on the <img> for height, vertical shift,
 * and the per-image shift cap. Once all are set, force the layer's
 * marquee to re-measure (its group width depends on these heights),
 * and only reveal the layer once the marquee reports ready again — so
 * the user never sees an in-progress measurement pass.
 * @param {element} $hero
 */
function randomize_images($hero) {
	const $layer = $hero.querySelector(images_layer_$);
	if (!$layer) return;

	const $images = Array.from($layer.querySelectorAll(image_$));
	if (!$images.length) return;

	const promises = $images.map($img => {
		return image_ready($img).then(() => {
			const ratio = ($img.naturalHeight > 0)
				? $img.naturalWidth / $img.naturalHeight
				: 1;
			const height = use_height_bias
				? compute_height(ratio)
				: compute_height_uniform();
			$img.style.setProperty(height_prop, `${height}px`);
			$img.style.setProperty(shift_prop, compute_shift().toFixed(3));
			$img.style.setProperty(shift_max_prop, `${compute_shift_max(height).toFixed(2)}%`);
		});
	});

	Promise.all(promises).then(() => {
		reveal_layer_when_marquee_ready($layer);
	});
}


/**
 * Force the layer's marquee to re-run `clone_groups()` (group width
 * depends on the freshly-set image heights), wait for it to flip
 * `data-marquee-ready` back to `true`, then reveal the layer.
 * Tripping `data-speed` is what triggers marquee.js's MutationObserver.
 * @param {element} $layer
 */
function reveal_layer_when_marquee_ready($layer) {
	const $marquee = $layer.querySelector(marquee_$);
	if (!$marquee) {
		$layer.setAttribute(images_ready_attr, g.true_s);
		return;
	}

	$marquee.setAttribute(last_width_attr, '0');
	$marquee.setAttribute(marquee_ready_attr, g.false_s);
	$marquee.setAttribute(speed_attr, $marquee.getAttribute(speed_attr));

	// Marquee may already have flipped back to ready synchronously
	// (the MutationObserver callback runs as a microtask, but the
	// effect could land before this line on a fast frame).
	if ($marquee.getAttribute(marquee_ready_attr) === g.true_s) {
		$layer.setAttribute(images_ready_attr, g.true_s);
		return;
	}

	const observer = new MutationObserver(() => {
		if ($marquee.getAttribute(marquee_ready_attr) !== g.true_s) return;
		observer.disconnect();
		$layer.setAttribute(images_ready_attr, g.true_s);
	});
	observer.observe($marquee, {
		attributes: true,
		attributeFilter: [marquee_ready_attr],
	});
}
